import { useState, useCallback } from "react";
import { FiMail, FiCheck, FiX, FiAlertCircle, FiXCircle } from "react-icons/fi";
import { cn } from "utils/helpers";
import { useAcceptInvitation } from "../../../../../hooks/usePanelScoringQueries";
import { Rfx } from "newui/features/Rfx/types/rfx.types";
import { DeclineDrawer } from "./DeclineDrawer";
import { PanelMemberStatus } from "newui/features/Rfx/Evaluation/types/panel.type";
import { EVALUATION_STEP_NUMBERS } from "newui/features/Rfx/Evaluation/constants/evaluation.constants";

interface InvitationBannerProps {
	currentStatus: PanelMemberStatus;
	currentStep: number;
	evalId: number;
	memberId: number;
	rfxData: Rfx;
	rfxTitle?: string;
	onAccept?: () => void;
	onDecline?: () => void;
}

export const InvitationBanner: React.FC<InvitationBannerProps> = ({
	currentStatus,
	currentStep,
	rfxData,
	evalId,
	memberId,
	rfxTitle,
	onAccept,
	onDecline,
}) => {

	if (!currentStatus) {
		return null;
	}

	const [accepted, setAccepted] = useState(currentStatus === "accepted");
	const [showDeclineDrawer, setShowDeclineDrawer] = useState(false);
	const acceptMutation = useAcceptInvitation(rfxData?.id, evalId);

	const handleAccept = useCallback(() => {
		acceptMutation.mutate(memberId, {
			onSuccess: () => {
				setAccepted(true);
				onAccept?.();
			},
		});
	}, [acceptMutation, memberId, onAccept]);

	const handleDeclined = useCallback(() => {
		setShowDeclineDrawer(true);
	}, [])

	const declineDrawer =
		<DeclineDrawer
			isOpen={showDeclineDrawer}
			onClose={() => setShowDeclineDrawer(false)}
			rfxId={rfxData?.id}
			evalId={evalId}
			memberId={memberId}
			rfxLeadName={rfxData.rfxLead?.fullName}
			rfxLeadEmail={rfxData.rfxLead?.email}
			rfxTitle={rfxData.name}
			onDeclined={onDecline}
		/>;

	if (accepted || currentStatus === "in_progress" || currentStatus === "submitted") {
		return (
			<div
				className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3"
				data-testid="invitation-accepted-banner"
			>
				<FiCheck size={18} className="shrink-0 text-green-600" aria-hidden="true" />
				<p className="text-sm text-green-700">
					You have accepted to score this Evaluation.
				</p>
				<button className="bg-red-600 rounded-md p-2 text-white hover:bg-red-800 text-sm" onClick={handleDeclined}>
					I can no longer participate
				</button>
				{declineDrawer}
			</div>
		);
	}

	if (currentStatus === "declined") {
		return (
			<div
				className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3"
				data-testid="invitation-declined-banner"
			>
				<FiXCircle size={18} className="shrink-0 text-red-600" aria-hidden="true" />
				<p className="text-sm text-red-700">
					You have declined to participate. You cannot score this Evaluation.
				</p>
				{currentStep < EVALUATION_STEP_NUMBERS.CLARIFICATIONS && <button className="bg-blue-600 rounded-md p-2 text-white hover:bg-blue-800 text-sm" onClick={handleAccept}>
					I want to participate
				</button>}
			</div>
		);
	}

	// catch all if not invited status
	if (currentStatus !== "invited") {
		return;
	}

	return (
		<div
			className="rounded-lg border border-[#0031DD]/20 bg-[#e8eeff] px-4 py-4"
			data-testid="invitation-banner"
		>
			<div className="flex items-start gap-3">
				<FiMail size={20} className="mt-0.5 shrink-0 text-[#0031DD]" aria-hidden="true" />
				<div className="flex-1">
					<p className="text-sm font-medium text-gray-900">
						You have been invited to participate in evaluating this RFx
					</p>
					{rfxTitle && (
						<p className="mt-0.5 text-xs text-gray-600">
							{rfxTitle}
						</p>
					)}
					<p className="mt-1.5 text-xs text-gray-500">
						Accept to begin scoring suppliers, or decline with a reason.
					</p>

					{(acceptMutation.isError) && (
						<p className="mt-2 inline-flex items-center gap-1 text-xs text-red-600">
							<FiAlertCircle size={12} />
							Failed to accept invitation. Please try again.
						</p>
					)}

					<div className="mt-3 flex items-center gap-2">
						<button
							type="button"
							onClick={handleAccept}
							disabled={acceptMutation.isPending}
							className={cn(
								"inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition-colors",
								"bg-[#0031DD] text-white hover:bg-[#0028b8] active:bg-[#002099]",
								"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0031DD] focus-visible:ring-offset-2",
								acceptMutation.isPending && "opacity-50 cursor-not-allowed"
							)}
							data-testid="accept-invitation-btn"
						>
							<FiCheck size={16} aria-hidden="true" />
							{acceptMutation.isPending ? "Accepting..." : "Accept"}
						</button>
						<button
							type="button"
							onClick={handleDeclined}
							disabled={acceptMutation.isPending}
							className={cn(
								"inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors",
								"hover:bg-gray-50 active:bg-gray-100",
								"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0031DD] focus-visible:ring-offset-2"
							)}
							data-testid="decline-invitation-btn"
						>
							<FiX size={16} aria-hidden="true" />
							Decline
						</button>
					</div>
				</div>
			</div>
			{declineDrawer}
		</div>
	);
};
